import { useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  orderBy, 
  limit, 
  onSnapshot, 
  addDoc, 
  deleteDoc,
  doc,
  where,
  getDocs,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db, appId } from '../config/firebase';

export const useSyncHistory = (userId, isAuthReady) => {
  const [syncHistory, setSyncHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isAuthReady || !userId || !db) {
      if (isAuthReady && !userId) {
        setSyncHistory([]);
      }
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    const syncHistoryPath = `artifacts/${appId}/users/${userId}/syncHistory`;

    const unsubscribe = onSnapshot(
      query(
        collection(db, syncHistoryPath),
        orderBy('timestamp', 'desc'),
        limit(50) // Load last 50 sync sessions
      ),
      (snap) => {
        const data = snap.docs.map((doc) => ({ 
          id: doc.id, 
          ...doc.data(),
          timestamp: doc.data().timestamp?.toDate() || new Date()
        }));
        setSyncHistory(data);
        setIsLoading(false);
        setError(null);
      },
      (err) => {
        console.error("Error fetching sync history:", err);
        setError("Could not fetch sync history.");
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [isAuthReady, userId]);

  const addSyncRecord = async (syncData) => {
    if (!db || !userId) {
      throw new Error("Database or user not available.");
    }

    // Only save if there were updates or errors
    if (syncData.totalUpdated === 0 && syncData.totalErrors === 0) {
      return null;
    }

    const syncHistoryPath = `artifacts/${appId}/users/${userId}/syncHistory`;
    
    const syncRecord = {
      timestamp: serverTimestamp(),
      duration: syncData.duration,
      totalChecked: syncData.totalChecked,
      totalUpdated: syncData.totalUpdated,
      totalErrors: syncData.totalErrors,
      changes: syncData.changes || [],
      errors: syncData.errors || []
    };

    try {
      const docRef = await addDoc(collection(db, syncHistoryPath), syncRecord);
      return docRef.id;
    } catch (error) {
      console.error("Error saving sync record:", error);
      throw error;
    }
  };

  const deleteSyncRecord = async (syncId) => {
    if (!db || !userId) {
      throw new Error("Database or user not available.");
    }

    const syncHistoryPath = `artifacts/${appId}/users/${userId}/syncHistory`;
    
    try {
      await deleteDoc(doc(db, syncHistoryPath, syncId));
    } catch (error) {
      console.error("Error deleting sync record:", error);
      throw error;
    }
  };

  const clearOldSyncHistory = async (daysToKeep = 90) => {
    if (!db || !userId) {
      throw new Error("Database or user not available.");
    }

    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysToKeep);
    
    const syncHistoryPath = `artifacts/${appId}/users/${userId}/syncHistory`;
    
    try {
      const q = query(
        collection(db, syncHistoryPath),
        where('timestamp', '<', Timestamp.fromDate(cutoffDate))
      );
      
      const snapshot = await getDocs(q);
      const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
      
      await Promise.all(deletePromises);
      return snapshot.docs.length;
    } catch (error) {
      console.error("Error clearing old sync history:", error);
      throw error;
    }
  };

  return {
    syncHistory,
    isLoading,
    error,
    addSyncRecord,
    deleteSyncRecord,
    clearOldSyncHistory
  };
};